/* eslint react-hooks/exhaustive-deps: "warn" */

import { type ReactNode, useCallback, useEffect, useState } from 'react';

import cn from 'classnames';
import Head from 'next/head';

import { Faq as FaqType } from 'libs/http/api/content/content.types';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

interface Props {
  className?: string;
  extra?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  data: FaqType[];
  onClick?: () => void;
}

const baseClass = 'asked-questions';

export const Faq = ({ className = '', extra, title, description, data, onClick }: Props) => {
  const [active, setActive] = useState<string[]>([]);

  useEffect(() => {
    setActive(
      data.reduce<typeof active>((acc, current) => {
        if (current.default_opened) {
          acc.push(current.id);
        }

        return acc;
      }, []),
    );
  }, [data]);

  const onToggle = useCallback(
    (id: (typeof active)[number]) => () => {
      if (typeof onClick === 'function') {
        onClick();
      }

      setActive((s) => (s.includes(id) ? s.filter((i) => i !== id) : [...s, id]));
    },
    [onClick],
  );

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: data
                .filter((item) => item.disabled !== true)
                .map((item) => ({
                  '@type': 'Question',
                  name: item.question,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: item.answer,
                  },
                })),
            }),
          }}
        />
      </Head>

      <section className={cn(`${baseClass}-wrapper`, className)}>
        <div className={`${baseClass}-container`}>
          <div className={`${baseClass}-text`}>
            <TitleBlock
              align="left"
              extra={extra}
              title={title}
              description={description}
              className={`${baseClass}-titleBlock`}
            />
          </div>

          <div className={`${baseClass}-list`}>
            {data.map((item) => {
              const isClosable = item.closable !== false;
              const isActive = !isClosable || active.includes(item.id);
              const isDisabled = item.disabled === true;

              return (
                <div key={item.id} className={cn(`${baseClass}-item`, isDisabled && 'question-disabled')}>
                  <h2 onClick={isClosable && isDisabled === false ? onToggle(item.id) : undefined}>{item.question}</h2>

                  {isClosable && <Icon type="arrow-2" className={isActive ? 'active' : 'unactive'} />}

                  {item.answer && (
                    <div className={isActive ? 'question-block-item' : 'question-block-item-hidden'}>
                      <div dangerouslySetInnerHTML={{ __html: item.answer }}></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};
